import React, { useState } from 'react';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/api';

const Container = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h2`
  color: #fff;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Card = styled.div`
  background: linear-gradient(135deg, #1a1f35 0%, #0d1117 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  padding: 2rem;
`;

const Step = styled.div`
  margin-bottom: 2rem;
`;

const StepTitle = styled.h3`
  color: #8b5cf6;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const StepDescription = styled.p`
  color: #a0aec0;
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const QrCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
  background: #fff;
  padding: 1rem;
  border-radius: 8px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
`;

const ManualCode = styled.div`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  margin: 1rem 0;
`;

const CodeText = styled.code`
  font-family: 'Fira Code', monospace;
  font-size: 1.2rem;
  color: #8b5cf6;
  letter-spacing: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1.5rem;
  text-align: center;
  letter-spacing: 8px;
  
  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
  }
  
  &::placeholder {
    letter-spacing: normal;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'secondary' ? `
    background: transparent;
    border: 1px solid rgba(139, 92, 246, 0.5);
    color: #8b5cf6;
    
    &:hover {
      background: rgba(139, 92, 246, 0.1);
    }
  ` : `
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const BackupCodesContainer = styled.div`
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
`;

const BackupCodesTitle = styled.h4`
  color: #fbbf24;
  margin-bottom: 0.5rem;
`;

const BackupCodesWarning = styled.p`
  color: #a0aec0;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const BackupCodesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
`;

const BackupCode = styled.code`
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem;
  border-radius: 4px;
  font-family: 'Fira Code', monospace;
  color: #fff;
  text-align: center;
`;

interface MfaSetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

type SetupStep = 'start' | 'qrcode' | 'verify' | 'complete';

const MfaSetup: React.FC<MfaSetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<SetupStep>('start');
  const [qrCodeDataUri, setQrCodeDataUri] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start MFA setup');
      }
      
      setQrCodeDataUri(data.qrCodeDataUri);
      setManualEntryKey(data.manualEntryKey);
      setStep('qrcode');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Invalid verification code');
      }
      
      setBackupCodes(data.backupCodes || []);
      setStep('complete');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const downloadBackupCodes = () => {
    const content = `OMS Platform - MFA Backup Codes\n${'='.repeat(40)}\n\nSave these codes in a secure location.\nEach code can only be used once.\n\n${backupCodes.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'oms-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <Card>
        <Title>🔐 Set Up Two-Factor Authentication</Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {step === 'start' && (
          <Step>
            <StepDescription>
              Two-factor authentication adds an extra layer of security to your account. 
              You'll need an authenticator app like Google Authenticator, Authy, or 1Password.
            </StepDescription>
            <Button onClick={startSetup} disabled={loading}>
              {loading ? 'Starting...' : 'Begin Setup'}
            </Button>
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} style={{ marginTop: '0.5rem' }}>
                Cancel
              </Button>
            )}
          </Step>
        )}
        
        {step === 'qrcode' && (
          <>
            <Step>
              <StepTitle>Step 1: Scan QR Code</StepTitle>
              <StepDescription>
                Open your authenticator app and scan this QR code:
              </StepDescription>
              <QrCodeContainer>
                <img src={qrCodeDataUri} alt="MFA QR Code" width="200" height="200" />
              </QrCodeContainer>
            </Step>
            
            <Step>
              <StepTitle>Can't scan? Enter manually:</StepTitle>
              <ManualCode>
                <CodeText>{manualEntryKey}</CodeText>
              </ManualCode>
            </Step>
            
            <Button onClick={() => setStep('verify')}>
              I've Scanned the Code
            </Button>
          </>
        )}
        
        {step === 'verify' && (
          <Step>
            <StepTitle>Step 2: Verify Code</StepTitle>
            <StepDescription>
              Enter the 6-digit code from your authenticator app:
            </StepDescription>
            <Input
              type="text"
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength={6}
              autoFocus
            />
            <div style={{ marginTop: '1rem' }}>
              <Button onClick={verifyCode} disabled={loading || verificationCode.length !== 6}>
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </Button>
              <Button variant="secondary" onClick={() => setStep('qrcode')} style={{ marginTop: '0.5rem' }}>
                Back
              </Button>
            </div>
          </Step>
        )}
        
        {step === 'complete' && (
          <>
            <SuccessMessage>
              ✅ Two-factor authentication has been enabled!
            </SuccessMessage>
            
            <BackupCodesContainer>
              <BackupCodesTitle>⚠️ Save Your Backup Codes</BackupCodesTitle>
              <BackupCodesWarning>
                If you lose access to your authenticator app, you can use these codes to sign in. 
                Each code can only be used once. Store them securely!
              </BackupCodesWarning>
              <BackupCodesList>
                {backupCodes.map((code, index) => (
                  <BackupCode key={index}>{code}</BackupCode>
                ))}
              </BackupCodesList>
            </BackupCodesContainer>
            
            <Button onClick={downloadBackupCodes} variant="secondary" style={{ marginBottom: '0.5rem' }}>
              Download Backup Codes
            </Button>
            <Button onClick={onComplete}>
              Done
            </Button>
          </>
        )}
      </Card>
    </Container>
  );
};

export default MfaSetup;
